/**
 * Utility functions for safe date formatting
 */

/**
 * Safely parses a date value and returns a Date object or null if invalid
 * @param {any} dateValue - The date value to parse (string, timestamp, or Date)
 * @returns {Date|null} - Valid Date object or null
 */
export const parseDate = (dateValue) => {
  if (!dateValue) return null;
  
  let date;
  
  // Handle Firestore timestamp format (both _seconds and seconds formats)
  if (typeof dateValue === 'object' && (dateValue._seconds || dateValue.seconds)) {
    const seconds = dateValue._seconds || dateValue.seconds;
    date = new Date(seconds * 1000);
  } else if (typeof dateValue === 'string') {
    // Handle custom format: "April 7, 2025 at 1:30:20 AM UTC"
    if (dateValue.includes(' at ') && dateValue.includes(' UTC')) {
      // Parse the custom format
      const customFormatRegex = /^(\w+ \d+, \d+) at (\d+:\d+:\d+ (?:AM|PM)) UTC$/;
      const match = dateValue.match(customFormatRegex);
      
      if (match) {
        const [, datePart, timePart] = match;
        // Reconstruct as a standard format that Date can parse
        const standardFormat = `${datePart} ${timePart} UTC`;
        date = new Date(standardFormat);
      } else {
        date = new Date(dateValue);
      }
    } else {
      date = new Date(dateValue);
    }
  } else if (dateValue instanceof Date) {
    // Handle Date objects directly
    date = dateValue;
  } else if (typeof dateValue === 'number') {
    // Handle Unix timestamps (seconds or milliseconds)
    date = new Date(dateValue > 1e10 ? dateValue : dateValue * 1000);
  } else {
    // Try to parse whatever it is
    date = new Date(dateValue);
  }
  
  // Check if the date is valid
  const isValid = date instanceof Date && !isNaN(date.getTime());
  
  // Debug logging for problematic dates (can be removed in production)
  if (!isValid && dateValue) {
    console.log('Failed to parse date:', dateValue, 'Type:', typeof dateValue, 'Value:', dateValue);
  }
  
  return isValid ? date : null;
};

/**
 * Formats a date safely with fallback text
 * @param {any} dateValue - The date value to format
 * @param {string} fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted date string or fallback text
 */
export const formatDate = (dateValue, fallbackText = 'Unknown date', options = {}) => {
  const date = parseDate(dateValue);
  if (!date) return fallbackText;
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Formats a date with time safely
 * @param {any} dateValue - The date value to format
 * @param {string} fallbackText - Text to show if date is invalid (default: 'Unknown date')
 * @returns {string} - Formatted date and time string or fallback text
 */
export const formatDateTime = (dateValue, fallbackText = 'Unknown date') => {
  const date = parseDate(dateValue);
  if (!date) return fallbackText;
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Checks if a date has expired (is in the past)
 * @param {any} dateValue - The date value to check
 * @returns {boolean} - True if expired, false if not or invalid
 */
export const isDateExpired = (dateValue) => {
  const date = parseDate(dateValue);
  if (!date) return false;
  return date < new Date();
};

/**
 * Gets a relative time string (e.g., "2 days ago", "in 3 hours")
 * @param {any} dateValue - The date value
 * @param {string} fallbackText - Text to show if date is invalid
 * @returns {string} - Relative time string or fallback text
 */
export const getRelativeTime = (dateValue, fallbackText = 'Unknown time') => {
  const date = parseDate(dateValue);
  if (!date) return fallbackText;
  
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
  if (diffInDays < 7) return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
  
  // For dates older than a week, show the formatted date
  return formatDate(date);
};
